/**
 * The ARIA 1.2 combobox pattern for a Google Places Autocomplete box (QA
 * 2026-09-25 #29).
 *
 * Google's legacy widget draws its suggestions in a `.pac-container` it
 * appends to <body>, highlights the row the arrow keys are on with
 * `.pac-item-selected`, and exposes none of it: to a screen reader the box
 * was a plain text field and the list did not exist. The widget already does
 * the keyboard work (Up / Down move through the rows, Enter picks, Escape
 * closes), so this only describes what it does:
 *
 * - the input is `role="combobox"`, `aria-autocomplete="list"`, with
 *   `aria-expanded` following the list and `aria-controls` naming it;
 * - the container is a labelled `role="listbox"`, each row a
 *   `role="option"` with an id and `aria-selected`;
 * - `aria-activedescendant` on the input follows the highlighted row, so
 *   the row is read out as the arrow keys move, while focus stays in the box.
 *
 * Which container is this input's: the one Google adds while this input's
 * Autocomplete is built (`claim`), found by diffing the page's containers
 * before and after, or failing that the next unclaimed one that appears.
 */

const CONTAINER = '.pac-container';
const ITEM = '.pac-item';
const SELECTED = 'pac-item-selected';
const CLAIM = 'data-combobox-for';

let counter = 0;

function visible(el: HTMLElement): boolean {
  const view = el.ownerDocument.defaultView;
  const display = view ? view.getComputedStyle(el).display : el.style.display;
  return display !== 'none' && !!el.querySelector(ITEM);
}

export interface PlacesCombobox {
  /** Call right before `new Autocomplete(input)`; returns `claim`. */
  snapshot: () => () => void;
  /** Stops watching and leaves the input as a plain combobox. */
  disconnect: () => void;
}

/**
 * Makes `input` a combobox now (before the Maps script is even loaded) and
 * returns the hooks to bind Google's list to it once the widget exists.
 */
export function wirePlacesCombobox(
  input: HTMLInputElement,
  listLabel: string,
): PlacesCombobox {
  counter += 1;
  const doc = input.ownerDocument;
  const listId = `${input.id || 'places'}-suggestions-${counter}`;
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-expanded', 'false');

  let container: HTMLElement | null = null;
  let listObserver: MutationObserver | null = null;
  let bodyObserver: MutationObserver | null = null;

  const sync = () => {
    if (!container) return;
    const items = Array.from(container.querySelectorAll<HTMLElement>(ITEM));
    let active = '';
    items.forEach((item, index) => {
      const id = `${listId}-option-${index}`;
      if (item.id !== id) item.setAttribute('id', id);
      if (item.getAttribute('role') !== 'option') {
        item.setAttribute('role', 'option');
      }
      const selected = item.classList.contains(SELECTED);
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      if (selected) active = id;
    });
    const open = visible(container);
    input.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open && active) input.setAttribute('aria-activedescendant', active);
    else input.removeAttribute('aria-activedescendant');
  };

  const adopt = (found: HTMLElement) => {
    container = found;
    found.setAttribute(CLAIM, listId);
    found.setAttribute('id', listId);
    found.setAttribute('role', 'listbox');
    found.setAttribute('aria-label', listLabel);
    input.setAttribute('aria-controls', listId);
    bodyObserver?.disconnect();
    bodyObserver = null;
    if (typeof MutationObserver !== 'undefined') {
      listObserver = new MutationObserver(sync);
      // Our own writes (id, role, aria-*) are not observed: no loop.
      listObserver.observe(found, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    }
    sync();
  };

  const unclaimed = (): HTMLElement[] => Array.from(
    doc.querySelectorAll<HTMLElement>(CONTAINER),
  ).filter((c) => !c.hasAttribute(CLAIM));

  const snapshot = () => {
    const before = new Set(unclaimed());
    return () => {
      const fresh = unclaimed().filter((c) => !before.has(c));
      if (fresh.length) {
        adopt(fresh[fresh.length - 1]);
        return;
      }
      // Google builds the container lazily in some versions: take the
      // first unclaimed one that turns up.
      if (typeof MutationObserver === 'undefined' || !doc.body) return;
      bodyObserver = new MutationObserver(() => {
        const next = unclaimed()[0];
        if (next) adopt(next);
      });
      bodyObserver.observe(doc.body, { childList: true });
    };
  };

  const disconnect = () => {
    listObserver?.disconnect();
    bodyObserver?.disconnect();
    listObserver = null;
    bodyObserver = null;
  };

  return { snapshot, disconnect };
}
